import { useState } from 'react';
import { Languages, Wand2, Upload, Video, Play, MoreHorizontal, X, CheckCircle, ChevronRight, FolderOpen, Youtube } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

interface DubbingPageProps {
  onNavigate?: (page: string) => void;
}

export function DubbingPage({ onNavigate }: DubbingPageProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showDubDetailDialog, setShowDubDetailDialog] = useState(false);
  const [selectedDub, setSelectedDub] = useState<any>(null);
  const [sourceTab, setSourceTab] = useState<'upload' | 'youtube' | 'tiktok' | 'instagram' | 'content-hub'>('upload');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [sourceLanguage, setSourceLanguage] = useState('detect');
  const [voiceCloningEnabled, setVoiceCloningEnabled] = useState(true);
  const [socialUrl, setSocialUrl] = useState('');
  const [selectedContentItems, setSelectedContentItems] = useState<number[]>([]);

  // Mock data for recent dubs
  const recentDubs = [
    {
      id: 1,
      name: '🥤 Kun päivät venyy toimistolla ja studiossa, välipalan...',
      language: 'English',
      targetLanguage: 'English',
      sourceLanguage: 'Finnish',
      createdAt: '7 seconds ago',
      status: 'processing' as const,
      source: 'https://www.tiktok.com/@example',
      thumbnail: 'https://images.unsplash.com/photo-1611003228941-98852ba62227',
      duration: '0:34',
    },
    {
      id: 2,
      name: 'Innehåller reklam för min webbshop! 🛒 ALLA våra formar finns nu åter...',
      language: 'French',
      targetLanguage: 'French',
      sourceLanguage: 'Swedish',
      createdAt: 'last week',
      status: 'completed' as const,
      source: 'https://www.tiktok.com/@example2',
      thumbnail: 'https://images.unsplash.com/photo-1583908701673-4cb5f290b548',
      duration: '0:28',
    },
    {
      id: 3,
      name: 'Borde jag bjuda ut henne änd?😭😭 #fördig #dejt #girl #prank #emailpran...',
      language: 'Portuguese',
      targetLanguage: 'Portuguese',
      sourceLanguage: 'Swedish',
      createdAt: 'last week',
      status: 'completed' as const,
      source: 'https://www.tiktok.com/@example3',
      thumbnail: 'https://images.unsplash.com/photo-1759303380841-55c09244fd2b',
      duration: '0:22',
    },
  ];

  // Mock content hub items
  const contentHubItems = [
    { id: 1, title: 'Summer Brunch Setup', thumbnail: 'https://images.unsplash.com/photo-1583908701673-4cb5f290b548', duration: '0:15', type: 'UGC' },
    { id: 2, title: 'Dessert Unboxing', thumbnail: 'https://images.unsplash.com/photo-1759303380841-55c09244fd2b', duration: '0:12', type: 'UGC' },
    { id: 3, title: 'Baking Process', thumbnail: 'https://images.unsplash.com/photo-1740836257337-0d4fd26db36b', duration: '0:20', type: 'UGC' },
    { id: 4, title: 'Product Review', thumbnail: 'https://images.unsplash.com/photo-1611003228941-98852ba62227', duration: '0:18', type: 'UGC' },
    { id: 5, title: 'Morning Routine', thumbnail: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af', duration: '0:25', type: 'UGC' },
    { id: 6, title: 'Workout Tips', thumbnail: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b', duration: '0:30', type: 'UGC' },
  ];

  const availableLanguages = [
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  ];

  const toggleLanguage = (code: string) => {
    setSelectedLanguages(prev =>
      prev.includes(code) ? prev.filter(l => l !== code) : [...prev, code]
    );
  };

  const toggleContentItem = (id: number) => {
    setSelectedContentItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleViewDub = (dub: any) => {
    setSelectedDub(dub);
    setShowDubDetailDialog(true);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#101828]">Dubbing</h1>
              <p className="text-gray-500 mt-1">AI-powered voice dubbing for your UGC content</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDialog(true)}
                className="gap-2"
              >
                <Wand2 className="w-4 h-4" />
                Bulk Dubbing
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#101828] hover:bg-gray-800 text-white gap-2"
              >
                Create a Dub
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Learn Section */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-gray-500 mb-4">Learn</h2>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-gray-200">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Play className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#101828] mb-1">Learn about the Dubbing editor</h3>
                  <p className="text-sm text-gray-500">A professional app for dubbing content</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-gray-200">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Youtube className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#101828] mb-1">Importing Social Media videos</h3>
                  <p className="text-sm text-gray-500">Learn how to dub any TikTok, Instagram, or YouTube video</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Recent Dubs */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-4">Recent Dubs</h2>
          <Card className="border-gray-200">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Name</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Language</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Created at</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {recentDubs.map((dub) => (
                  <tr
                    key={dub.id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewDub(dub)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={dub.thumbnail} alt={dub.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <Play className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <div className="max-w-md">
                          <p className="text-sm text-[#101828] truncate">{dub.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{dub.language}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{dub.createdAt}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {dub.status === 'processing' ? (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-0">
                          Processing
                        </Badge>
                      ) : (
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {/* Create Dub Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border-2 border-gray-200 flex items-center justify-center">
                <Languages className="w-5 h-5" />
              </div>
              Create a dub
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Source Language */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-2">Source Language</label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={sourceLanguage}
                  onChange={(e) => setSourceLanguage(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="detect">Detect</option>
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
                <div className="relative">
                  <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between">
                    <span className="text-gray-500">Select languages</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Audio or Video Source */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-3">Audio or video source</label>
              <div className="flex gap-2 mb-4">
                <Button
                  variant={sourceTab === 'upload' ? 'default' : 'outline'}
                  onClick={() => setSourceTab('upload')}
                  className={sourceTab === 'upload' ? 'bg-[#101828]' : ''}
                  size="sm"
                >
                  Upload
                </Button>
                <Button
                  variant={sourceTab === 'youtube' ? 'default' : 'outline'}
                  onClick={() => setSourceTab('youtube')}
                  className={sourceTab === 'youtube' ? 'bg-[#101828]' : ''}
                  size="sm"
                >
                  YouTube
                </Button>
                <Button
                  variant={sourceTab === 'tiktok' ? 'default' : 'outline'}
                  onClick={() => setSourceTab('tiktok')}
                  className={sourceTab === 'tiktok' ? 'bg-[#101828]' : ''}
                  size="sm"
                >
                  TikTok
                </Button>
                <Button
                  variant={sourceTab === 'instagram' ? 'default' : 'outline'}
                  onClick={() => setSourceTab('instagram')}
                  className={sourceTab === 'instagram' ? 'bg-[#101828]' : ''}
                  size="sm"
                >
                  Instagram
                </Button>
                <Button
                  variant={sourceTab === 'content-hub' ? 'default' : 'outline'}
                  onClick={() => setSourceTab('content-hub')}
                  className={sourceTab === 'content-hub' ? 'bg-[#101828]' : ''}
                  size="sm"
                >
                  Content Hub
                </Button>
              </div>

              {/* Upload Tab */}
              {sourceTab === 'upload' && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-purple-400 transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-[#101828] mb-1">Click or drag to upload here</p>
                  <p className="text-xs text-gray-500">Audio or video file, up to 1000MB or 45 minutes</p>
                </div>
              )}

              {/* Social Media URL Tabs */}
              {(sourceTab === 'youtube' || sourceTab === 'tiktok' || sourceTab === 'instagram') && (
                <div>
                  <input
                    type="text"
                    placeholder={`Paste ${sourceTab.charAt(0).toUpperCase() + sourceTab.slice(1)} URL here`}
                    value={socialUrl}
                    onChange={(e) => setSocialUrl(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Example: https://www.{sourceTab}.com/@username/video/...
                  </p>
                </div>
              )}

              {/* Content Hub Tab */}
              {sourceTab === 'content-hub' && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-[#101828]">Select from Content Hub</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onNavigate && onNavigate('content-hub')}
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Browse All
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto">
                    {contentHubItems.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => toggleContentItem(item.id)}
                        className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          selectedContentItems.includes(item.id)
                            ? 'border-purple-600 ring-2 ring-purple-200'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img src={item.thumbnail} alt={item.title} className="w-full h-24 object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-2 right-2">
                          <p className="text-xs text-white font-medium truncate">{item.title}</p>
                          <p className="text-xs text-white/80">{item.duration}</p>
                        </div>
                        {selectedContentItems.includes(item.id) && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedContentItems.length > 0 && (
                    <p className="text-sm text-purple-600 mt-3">
                      {selectedContentItems.length} item(s) selected
                    </p>
                  )}
                </div>
              )}

              {sourceTab === 'upload' && (
                <p className="text-xs text-gray-500 mt-3">
                  Your video will be dubbed in standard resolution and will include a watermark.
                  Only Creator+ plans can change this.{' '}
                  <button className="text-purple-600 hover:underline">Upgrade your plan</button>
                </p>
              )}
            </div>

            {/* Target Languages */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-3">Target Languages</label>
              <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedLanguages.includes(lang.code)
                        ? 'bg-purple-100 text-purple-700 border-2 border-purple-600'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </button>
                ))}
              </div>
              {selectedLanguages.length > 0 && (
                <p className="text-sm text-purple-600 mt-2">
                  {selectedLanguages.length} language(s) selected
                </p>
              )}
            </div>

            {/* Number of Speakers */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-2">Number of speakers</label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm">
                <option>Detect</option>
                <option>1</option>
                <option>2</option>
                <option>3+</option>
              </select>
            </div>

            {/* Time Range */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-2">Time range to dub</label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="hh:mm:ss"
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="text"
                  placeholder="hh:mm:ss"
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Voice Cloning Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#101828]">Disable voice cloning</label>
              <button
                onClick={() => setVoiceCloningEnabled(!voiceCloningEnabled)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  voiceCloningEnabled ? 'bg-gray-300' : 'bg-purple-600'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    voiceCloningEnabled ? 'translate-x-1' : 'translate-x-6'
                  }`}
                />
              </button>
            </div>

            {/* Create Button */}
            <Button
              className="w-full bg-[#101828] hover:bg-gray-800 text-white py-6"
              disabled={selectedLanguages.length === 0 && selectedContentItems.length === 0}
            >
              Create dub
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Dubbing Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              Bulk Dubbing
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            <p className="text-sm text-gray-600">
              Dub multiple videos at once to save time. Select videos from your Content Hub and choose target languages.
            </p>

            {/* Content Selection */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-[#101828]">Select Videos</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onNavigate && onNavigate('content-hub')}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Browse Content Hub
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {contentHubItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => toggleContentItem(item.id)}
                    className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      selectedContentItems.includes(item.id)
                        ? 'border-purple-600 ring-2 ring-purple-200'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={item.thumbnail} alt={item.title} className="w-full h-32 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-xs text-white font-medium truncate">{item.title}</p>
                      <p className="text-xs text-white/80">{item.duration}</p>
                    </div>
                    {selectedContentItems.includes(item.id) && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {selectedContentItems.length > 0 && (
                <p className="text-sm font-medium text-purple-600 mt-3">
                  {selectedContentItems.length} video(s) selected
                </p>
              )}
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-[#101828] mb-3">Target Languages</label>
              <div className="grid grid-cols-4 gap-2 p-4 border border-gray-200 rounded-lg">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedLanguages.includes(lang.code)
                        ? 'bg-purple-100 text-purple-700 border-2 border-purple-600'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            {selectedContentItems.length > 0 && selectedLanguages.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">Processing Summary</h4>
                <div className="space-y-1 text-sm text-purple-800">
                  <p>• {selectedContentItems.length} videos will be dubbed</p>
                  <p>• {selectedLanguages.length} languages selected</p>
                  <p>• Total: {selectedContentItems.length * selectedLanguages.length} dubs will be created</p>
                  <p className="text-xs text-purple-700 mt-2">
                    Estimated processing time: ~{selectedContentItems.length * selectedLanguages.length * 2} minutes
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                disabled={selectedContentItems.length === 0 || selectedLanguages.length === 0}
              >
                Start Bulk Dubbing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dub Detail Dialog */}
      <Dialog open={showDubDetailDialog} onOpenChange={setShowDubDetailDialog}>
        <DialogContent className="max-w-3xl">
          {selectedDub && (
            <>
              <DialogHeader>
                <div className="absolute top-4 right-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDubDetailDialog(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Video Player */}
                <div className="relative w-full aspect-[9/16] max-h-[500px] bg-black rounded-lg overflow-hidden">
                  <img src={selectedDub.thumbnail} alt={selectedDub.name} className="w-full h-full object-contain" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Button className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30">
                      <Play className="w-8 h-8 text-white" />
                    </Button>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm">
                      0:00 / {selectedDub.duration}
                    </div>
                  </div>
                </div>

                {/* Title and Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-[#101828] mb-2">{selectedDub.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{selectedDub.createdAt}</p>
                  <div className="flex gap-3">
                    <Button variant="outline" className="gap-2">
                      <Video className="w-4 h-4" />
                      Edit in Studio
                    </Button>
                    <Button className="bg-[#101828] hover:bg-gray-800 text-white">Download</Button>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Source Language</p>
                    <p className="text-sm text-[#101828]">{selectedDub.sourceLanguage}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Target Language</p>
                    <p className="text-sm text-[#101828]">{selectedDub.targetLanguage}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Source</p>
                    <a href={selectedDub.source} className="text-sm text-purple-600 hover:underline truncate block">
                      {selectedDub.source}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Number of Speakers</p>
                    <p className="text-sm text-[#101828]">Auto-Detect</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Video Resolution</p>
                    <p className="text-sm text-[#101828]">Medium</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Watermarked</p>
                    <p className="text-sm text-[#101828]">Yes</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Dubbed Range</p>
                    <p className="text-sm text-[#101828]">Start - End</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Credits Used</p>
                    <p className="text-sm text-[#101828]">1141.6946334089191</p>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">Dubbing ID</p>
                  <p className="text-xs text-gray-600 font-mono">9Q3BrACQJA5vWQa7f3BX</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}